import axios from "axios";
import React, { useEffect, useState } from "react";
import { FaTrash, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/menu/master/`;

const DeleteMenuTree = () => {
    const [menus, setMenus] = useState([]);
    const navigate = useNavigate();

    const fetchMenus = async () => {
        try {
            const response = await axios.get(`${API_URL}`, { withCredentials: true });
            setMenus(response.data);
        } catch(error) {
            console.error("Failed to load menu tree:", error);
        }
    };

    useEffect(() => {
        fetchMenus();
    }, []);

    const handleDelete = (id, title) => {
        Swal.fire({
            title: `Delete "${title}"?`,
            text: "This will remove the menu and its children!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e3342f",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if(result.isConfirmed) {
                try {
                    const response = await axios.delete(`${API_URL}${id}`, { withCredentials: true });
                    if(response.status === 200 || response.status === 204) {
                        Swal.fire(
                            "Deleted!",
                            `"${title}" was removed.`,
                            "success",
                        );

                        // Emit a custom event to notify the sidebar
                        window.dispatchEvent(new Event("menu-updated"));

                        // Refresh the menu tree
                        fetchMenus();
                    } else {
                        console.error(
                            "Unexpected response status:",
                            response.status,
                        );
                        Swal.fire(
                            "Error",
                            "Could not delete the menu.",
                            "error",
                        );
                    }
                } catch(error) {
                    console.error("Delete failed:", error);
                    if(error.response) {
                        console.error(
                            "Error response data:",
                            error.response.data,
                        );
                        console.error(
                            "Error response status:",
                            error.response.status,
                        );
                        console.error(
                            "Error response headers:",
                            error.response.headers,
                        );
                    }
                    Swal.fire("Error", "Could not delete the menu.", "error");
                }
            }
        });
    };

    // Recursive menu renderer
    const renderTree = (items, level = 0) => (
        <ul className="pl-4 border-l border-gray-200">
            {items.map((item) => (
                <li key={item.id} className="mb-2">
                    <div className="flex justify-between items-center group px-1 py-1 rounded hover:bg-gray-100">
                        <span className="text-gray-800 ml-1">{item.title}</span>
                        <button
                            onClick={() => handleDelete(item.id, item.title)}
                            className="text-red-600 hover:text-red-800 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete"
                        >
                            <FaTrash />
                        </button>
                    </div>
                    {item.children && item.children.length > 0 && (
                        <div className="ml-4">
                            {renderTree(item.children, level + 1)}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );

    return (
        <div className="container mx-auto p-6">
            {/* Breadcrumb */}
            <nav className="text-sm mb-4">
                <ul className="flex space-x-2 text-gray-600">
                    <li
                        className="cursor-pointer hover:text-blue-600"
                        onClick={() => navigate("/")}
                    >
                        Home
                    </li>
                    <li>/</li>
                    <li className="font-semibold text-gray-800">
                        Delete Menu Items
                    </li>
                </ul>
            </nav>

            {/* Page content */}
            <div className="flex justify-center items-center min-h-screen p-4">
                <div className="w-full max-w-3xl bg-white shadow-xl rounded-lg p-6 border border-gray-200">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-3xl font-bold text-gray-800">
                            Delete Menu Items
                        </h1>
                        <button
                            className="flex items-center bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition"
                            onClick={() => navigate("/admin")}
                        >
                            <FaArrowLeft className="mr-2" /> Back
                        </button>
                    </div>

                    <p className="text-gray-600 mb-4">
                        Click the 🗑️ icon next to a menu item to delete it and
                        its children.
                    </p>

                    <div className="bg-gray-50 p-4 rounded-md shadow-inner border border-gray-200 max-h-[70vh] overflow-y-auto">
                        {menus.length > 0 ? (
                            renderTree(menus)
                        ) : (
                            <p>No menus found.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteMenuTree;
